import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { USER_ROLES } from '../../modules/ac/constants'
import { selectAC } from '../selectors/AC'

const FALLBACK_ROLE_IDS = Object.values(USER_ROLES)

/**
 * Provides access to roles for display (all roles, including non-assignable ones).
 */
const useRoles = (roleIds = null, asMap = false) => {
  const { t } = useTranslation()
  const { roles, rolesById } = useSelector(selectAC)

  const roleCatalog = useMemo(() => {
    if (roles?.length) {
      return roles
    }
    return FALLBACK_ROLE_IDS.map(id => ({ id, name: id, assignableInInterface: id !== USER_ROLES.API }))
  }, [roles])

  const roleDetailsMap = useMemo(() => {
    if (roles?.length) {
      return rolesById
    }
    return roleCatalog.reduce((map, role) => {
      map[role.id] = role
      return map
    }, {})
  }, [roleCatalog, roles, rolesById])

  const mapRoleToReturnedRole = useCallback(
    role => {
      const id = typeof role === 'string' ? role : role?.id
      const apiName = roleDetailsMap[id]?.name
      return {
        id,
        name: t(`USER_ROLES['${id}']`, apiName || id),
        assignableInInterface: roleDetailsMap[id]?.assignableInInterface !== false
      }
    },
    [t, roleDetailsMap]
  )

  return useMemo(() => {
    if (roleIds === null) {
      const itemArray = roleCatalog.map(mapRoleToReturnedRole)
      return asMap
        ? itemArray.reduce((map, role) => {
            map[role.id] = role
            return map
          }, {})
        : itemArray
    }

    if (typeof roleIds === 'string' || typeof roleIds === 'number') {
      return mapRoleToReturnedRole(roleIds)
    }

    if (Array.isArray(roleIds)) {
      const itemArray = roleIds
        .map(mapRoleToReturnedRole)
        .filter(Boolean)
      return asMap
        ? itemArray.reduce((map, role) => {
            map[role.id] = role
            return map
          }, {})
        : itemArray
    }

    return asMap ? {} : []
  }, [roleIds, asMap, roleCatalog, mapRoleToReturnedRole])
}

export default useRoles
